import { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    List, ListItem, ListItemButton, ListItemText, Divider, IconButton, Box, Typography,
    FormControl, InputLabel, Select, MenuItem, CircularProgress,
    TextField, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { TOB_INSURERS, MY_HEALTH_INSURERS_CONFIG, buildMyHealthCompositePlan } from '../utils/tobInsurers';
import { fetchCustomTobPlans, fetchCustomTobPlan } from '../services/CustomTOBService';
import { toast } from 'react-toastify';

const MY_HEALTH_DEFAULT_MODULE_TIERS = { op: '', ma: '', dn: '' };
const MY_HEALTH_MODULE_FIELDS = [
    { key: 'op', label: 'Outpatient' },
    { key: 'ma', label: 'Maternity' },
    { key: 'dn', label: 'Dental' },
];

const CUSTOM_TOB_INSURER = { key: 'custom_tob_plans', label: 'My TOB Plans', isCustom: true };

export default function AddTobDialog({ open, onClose, onSubmit, existingPlanIds = [], token, onAddCustomTob, customTobFormOpen }) {
    const [selectedInsurer, setSelectedInsurer] = useState(null);
    const [myHealthHsId, setMyHealthHsId] = useState('');
    const [myHealthModuleTiers, setMyHealthModuleTiers] = useState(MY_HEALTH_DEFAULT_MODULE_TIERS);
    const [customTobPlans, setCustomTobPlans] = useState([]);
    const [loadingCustomTobPlans, setLoadingCustomTobPlans] = useState(false);
    const [addingCustomTobPlanId, setAddingCustomTobPlanId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const hasLoadedCustomTobPlansRef = useRef(false);

    useEffect(() => {
        if (open) {
            setSelectedInsurer(null);
            setSearchQuery('');
        }
    }, [open]);

    const myHealthConfig = selectedInsurer && !selectedInsurer.isCustom ? MY_HEALTH_INSURERS_CONFIG[selectedInsurer.key] : null;

    function loadCustomTobPlans() {
        const isFirstLoad = !hasLoadedCustomTobPlansRef.current;
        if (isFirstLoad) setLoadingCustomTobPlans(true);

        fetchCustomTobPlans({ token, listOnly: true })
            .then(response => {
                setCustomTobPlans(response.data.custom_tob_plans || []);
                hasLoadedCustomTobPlansRef.current = true;
            })
            .catch(() => toast.error('Failed to load custom TOB plans.'))
            .finally(() => { if (isFirstLoad) setLoadingCustomTobPlans(false); });
    }

    // The custom TOB form opens on top of this dialog, so once it closes the list is
    // refreshed to pick up whatever plans were added or renamed there.
    const wasCustomTobFormOpenRef = useRef(customTobFormOpen);
    useEffect(() => {
        const justClosed = wasCustomTobFormOpenRef.current && !customTobFormOpen;
        wasCustomTobFormOpenRef.current = customTobFormOpen;

        if (justClosed && open && selectedInsurer?.isCustom) loadCustomTobPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customTobFormOpen]);

    function handleSelectInsurer(insurer) {
        setSelectedInsurer(insurer);
        setSearchQuery('');

        if (MY_HEALTH_INSURERS_CONFIG[insurer.key]) {
            setMyHealthHsId(MY_HEALTH_INSURERS_CONFIG[insurer.key].hsOptions[0]?.id || '');
            setMyHealthModuleTiers(MY_HEALTH_DEFAULT_MODULE_TIERS);
        }

        if (insurer.isCustom) loadCustomTobPlans();
    }

    function handleSelectPlan(plan) {
        onSubmit({ id: plan.id, label: `${selectedInsurer.label} ${plan.label}` });
        onClose();
    }

    async function handleSelectCustomTobPlan(plan) {
        setAddingCustomTobPlanId(plan.id);
        try {
            const response = await fetchCustomTobPlan({ id: plan.id, token });
            const fullPlan = response.data.custom_tob_plan;
            onSubmit({
                id: fullPlan.plan_id,
                label: fullPlan.label,
                table: fullPlan.table,
                vertical_merge_cells: fullPlan.vertical_merge_cells,
            });
            onClose();
        } catch (error) {
            toast.error('Failed to load the selected custom TOB plan.');
        } finally {
            setAddingCustomTobPlanId(null);
        }
    }

    function handleAddCustomTob() {
        onAddCustomTob?.();
    }

    function handleAddMyHealthPlan() {
        const plan = buildMyHealthCompositePlan(selectedInsurer.key, myHealthHsId, myHealthModuleTiers);

        if (!plan) return;

        handleSelectPlan(plan);
    }

    const title = selectedInsurer ? selectedInsurer.label : 'Select Insurer';

    const query = searchQuery.trim().toLowerCase();
    const visibleInsurers = [CUSTOM_TOB_INSURER, ...TOB_INSURERS].filter(i => i.label.toLowerCase().includes(query));
    const visibleCustomTobPlans = customTobPlans.filter(p => p.label.toLowerCase().includes(query));
    const visiblePlans = selectedInsurer && !selectedInsurer.isCustom && !myHealthConfig
        ? selectedInsurer.plans.filter(p => p.label.toLowerCase().includes(query))
        : [];

    const showSearch = !selectedInsurer
        || (selectedInsurer.isCustom && !loadingCustomTobPlans && customTobPlans.length > 0)
        || (!selectedInsurer.isCustom && !myHealthConfig);

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 7,
                    py: 1.5,
                    px: 1.5,
                    backgroundColor: '#f1f4fa',
                },
            }}
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedInsurer && (
                        <IconButton size="small" onClick={() => { setSelectedInsurer(null); setSearchQuery(''); }} sx={{ ml: -1 }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Typography variant="h6" component="span">{title}</Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {showSearch && (
                    <Box sx={{ px: 2, pb: 1, mt: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder={selectedInsurer ? 'Search plans' : 'Search insurers'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 5, backgroundColor: 'transparent' } }}
                        />
                    </Box>
                )}
                {!selectedInsurer ? (
                    <List disablePadding>
                        {visibleInsurers.map((insurer, index) => (
                            <Box key={insurer.key}>
                                {index > 0 && <Divider />}
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleSelectInsurer(insurer)}
                                    >
                                        <ListItemText
                                            primary={insurer.label}
                                            primaryTypographyProps={insurer.isCustom ? { fontWeight: 600, color: '#2196f3' } : undefined}
                                        />
                                        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                                    </ListItemButton>
                                </ListItem>
                            </Box>
                        ))}
                        {visibleInsurers.length === 0 && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
                                No insurers found
                            </Typography>
                        )}
                    </List>
                ) : selectedInsurer.isCustom ? (
                    loadingCustomTobPlans ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : customTobPlans.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, px: 2, py: 3 }}>
                            <Typography color="text.secondary">
                                No custom TOB plans found.
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleAddCustomTob}
                                sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' } }}
                            >
                                Add Custom TOB
                            </Button>
                        </Box>
                    ) : (
                        <List disablePadding>
                            <ListItem disablePadding>
                                <ListItemButton onClick={handleAddCustomTob}>
                                    <AddIcon sx={{ color: '#2196f3', mr: 1 }} fontSize="small" />
                                    <ListItemText
                                        primary="Add New Plan"
                                        primaryTypographyProps={{ fontWeight: 600, color: '#2196f3' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                            {visibleCustomTobPlans.map((plan) => (
                                <Box key={plan.id}>
                                    <Divider />
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => handleSelectCustomTobPlan(plan)}
                                            disabled={addingCustomTobPlanId === plan.id}
                                        >
                                            <ListItemText primary={plan.label} />
                                            {addingCustomTobPlanId === plan.id && <CircularProgress size={18} />}
                                        </ListItemButton>
                                    </ListItem>
                                </Box>
                            ))}
                            {visibleCustomTobPlans.length === 0 && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
                                    No plans found
                                </Typography>
                            )}
                        </List>
                    )
                ) : myHealthConfig ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: 1 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="my-health-hs-label">Hospital &amp; Surgery</InputLabel>
                            <Select
                                labelId="my-health-hs-label"
                                label="Hospital & Surgery"
                                value={myHealthHsId}
                                onChange={(e) => setMyHealthHsId(e.target.value)}
                            >
                                {myHealthConfig.hsOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {MY_HEALTH_MODULE_FIELDS.map((field) => (
                            <FormControl key={field.key} size="small" fullWidth>
                                <InputLabel id={`my-health-${field.key}-label`}>{field.label}</InputLabel>
                                <Select
                                    labelId={`my-health-${field.key}-label`}
                                    label={field.label}
                                    value={myHealthModuleTiers[field.key]}
                                    onChange={(e) => setMyHealthModuleTiers((prev) => ({ ...prev, [field.key]: e.target.value }))}
                                >
                                    <MenuItem value="">N/A</MenuItem>
                                    {myHealthConfig.moduleTierOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}
                    </Box>
                ) : (
                    <List disablePadding>
                        {visiblePlans.map((plan, index) => (
                            <Box key={plan.id}>
                                {index > 0 && <Divider />}
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => handleSelectPlan(plan)}>
                                        <ListItemText primary={plan.label} />
                                    </ListItemButton>
                                </ListItem>
                            </Box>
                        ))}
                        {visiblePlans.length === 0 && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
                                No plans found
                            </Typography>
                        )}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {myHealthConfig && (
                    <Button variant="contained" onClick={handleAddMyHealthPlan} disabled={!myHealthHsId}>Add</Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
